// Detect an accepted answer spoken within a think-aloud transcript (or typed as part of a sentence).
//
// Word-window match: slide over the transcript's words and compare each window against each accepted
// form. Comparing whole words (not raw substrings) stops short answers matching inside longer words
// ("art" never matches "start"). Returns the matched canonical form, or null.
//
// Matching is deliberately asymmetric by answer length:
//   • ALWAYS  — exact spelling of the joined window (also absorbs spacing: "down town" == "downtown").
//   • MULTI-WORD answers ONLY — a phonetic/fuzzy pass (metaphone within ~1 edit) so ASR homophones
//     like "no i deer" ~ "no idea" still resolve. We DON'T apply this to single short words, because
//     phonetic hashing drops vowels — TAP/TOP/TIP all reduce to the same code, so it would over-match
//     distinct words. Single-word homophones the ASR mangles are handled per-puzzle via spokenVariants.

import { soundsLike } from './phonetic'

const wordsOf = (s: string): string[] => s.toLowerCase().match(/[a-z0-9]+/g) || []

// Filler said around the answer that shouldn't block a match if it lands inside a window.
const STOP = new Set(['commit'])

export const matchSpokenAnswer = (text: string, acceptedForms: string[]): string | null => {
  const w = wordsOf(text).filter((x) => !STOP.has(x))
  if (!w.length) return null
  for (const form of acceptedForms) {
    const fw = wordsOf(form)
    if (!fw.length) continue
    const target = fw.join('')
    const targetAlpha = target.replace(/[^a-z]/g, '') // letters only — metaphone ignores digits/spacing
    const allowPhonetic = fw.length >= 2 // multi-word only — see note above
    // ±1 word vs the canonical word-count absorbs spacing differences between speech and the answer.
    const lo = Math.max(1, fw.length - 1)
    const hi = fw.length + 1
    for (let len = lo; len <= hi; len++) {
      for (let i = 0; i + len <= w.length; i++) {
        const win = w.slice(i, i + len).join('')
        if (win === target) return form
        // Phonetic pass only when the spoken chunk is roughly as long (in letters) as the answer.
        // Otherwise a short fragment fuzzy-matches a longer answer — e.g. "time" (metaphone TM) is
        // within 1 edit of "teatime" (TTM), so "time 40" wrongly matched TEATIME.
        if (allowPhonetic && win.replace(/[^a-z]/g, '').length >= targetAlpha.length - 1 && soundsLike(win, target)) return form
      }
    }
  }
  return null
}
